import React, { useEffect, useRef, useState, useMemo } from "react";
import { init, dispose } from "klinecharts";
import { Maximize2, Minimize2, Palette, Columns2, X, RotateCcw, Layers, Zap } from "lucide-react";
import { DEFAULT_ACTIVE, INDICATOR_MAP } from "@/lib/indicators";
import { loadZoneStyles, saveZoneStyles } from "@/lib/zoneStyles";
import ColorSwatchPicker from "@/components/ColorSwatchPicker";
import IndicatorsMenu from "@/components/IndicatorsMenu";
import TemplatesMenu from "@/components/TemplatesMenu";
import DrawingMenu from "@/components/DrawingMenu";
import useChartOverlays from "@/hooks/useChartOverlays";

/**
 * Institutional-grade candlestick chart powered by KLineCharts v9 (Apache 2.0, no watermark).
 *
 * Features:
 * - Fully customizable theme (persisted in localStorage `qtr-chart-theme`)
 * - Full-screen mode (Fullscreen API)
 * - Multi-timeframe split view with crosshair sync
 * - SVG overlay for institutional zone boxes (ZII / ZRI / DP), RL price lines, kill-zones and markers
 * - Volume histogram in a secondary pane
 */

const DEFAULT_THEME = {
  bullish: "#00e676",
  bearish: "#ff3b30",
  wick: "auto",
  background: "#0b1420",
  grid: "#1e293b",
  text: "rgba(255,255,255,0.7)",
  kzAsia: "#8b5cf6",
  kzLondon: "#38bdf8",
  kzNyAm: "#22c55e",
  kzNyPm: "#f59e0b",
  impulseBuy: "#10b981",
  impulseSell: "#f43f5e",
};

const PRESETS = {
  anatradex: { ...DEFAULT_THEME },
  darkpro: { bullish: "#2e7d32", bearish: "#b03a2e", wick: "auto", background: "#0d1117", grid: "#161d27", text: "rgba(255,255,255,0.68)" },
  midnight: { bullish: "#22d3ee", bearish: "#f43f5e", wick: "auto", background: "#050a13", grid: "#0f172a", text: "rgba(255,255,255,0.75)" },
  paper: { bullish: "#26a69a", bearish: "#ef5350", wick: "auto", background: "#f7f8fa", grid: "#e2e8f0", text: "rgba(0,0,0,0.75)" },
  neon: { bullish: "#a3e635", bearish: "#ec4899", wick: "auto", background: "#0a0118", grid: "#1e1b4b", text: "rgba(255,255,255,0.85)" },
};

function loadTheme() {
  try {
    const raw = localStorage.getItem("qtr-chart-theme");
    if (raw) return { ...DEFAULT_THEME, ...JSON.parse(raw) };
  } catch { /* noop */ }
  return DEFAULT_THEME;
}

function saveTheme(theme) {
  try { localStorage.setItem("qtr-chart-theme", JSON.stringify(theme)); } catch { /* noop */ }
}

function normalizeCandles(candles) {
  if (!candles || !candles.length) return [];
  return candles.map((c) => {
    let t = c.time ?? c.timestamp ?? c.t;
    if (typeof t === "string") t = new Date(t).getTime();
    else if (t < 1e12) t = t * 1000;
    return {
      timestamp: t,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
      volume: c.volume ?? Math.max(1, Math.abs(c.close - c.open) * 1000),
    };
  }).sort((a, b) => a.timestamp - b.timestamp);
}

function buildStyles(theme) {
  const isLight = theme.background.startsWith("#f") || theme.background.startsWith("#e");
  const axisTextColor = isLight ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.65)";
  return {
    grid: {
      show: true,
      horizontal: { show: true, color: theme.grid, style: "dashed", dashedValue: [2, 2] },
      vertical:   { show: true, color: theme.grid, style: "dashed", dashedValue: [2, 2] },
    },
    candle: {
      bar: {
        upColor: theme.bullish,
        downColor: theme.bearish,
        noChangeColor: theme.text,
        upBorderColor: theme.bullish,
        downBorderColor: theme.bearish,
        upWickColor: theme.wick === "auto" ? theme.bullish : theme.wick,
        downWickColor: theme.wick === "auto" ? theme.bearish : theme.wick,
      },
      tooltip: {
        text: { color: axisTextColor, size: 11, family: "IBM Plex Mono, ui-monospace, monospace" },
      },
      priceMark: {
        last: {
          show: true,
          upColor: theme.bullish,
          downColor: theme.bearish,
          text: { show: true, color: "#ffffff", size: 11, family: "IBM Plex Mono" },
        },
      },
    },
    indicator: {
      bars: [{ style: "fill", borderStyle: "solid", borderSize: 1, borderDashedValue: [], upColor: `${theme.bullish}55`, downColor: `${theme.bearish}55`, noChangeColor: theme.text }],
      lines: [{ style: "solid", smooth: false, size: 1, dashedValue: [2, 2], color: theme.bullish }],
    },
    xAxis: {
      show: true,
      axisLine: { show: true, color: theme.grid, size: 1 },
      tickLine: { show: true, color: theme.grid, size: 1, length: 3 },
      tickText: { show: true, color: axisTextColor, family: "IBM Plex Mono", size: 10, weight: "normal", marginStart: 4, marginEnd: 4 },
    },
    yAxis: {
      show: true,
      position: "right",
      axisLine: { show: true, color: theme.grid, size: 1 },
      tickLine: { show: true, color: theme.grid, size: 1, length: 3 },
      tickText: { show: true, color: axisTextColor, family: "IBM Plex Mono", size: 10, weight: "normal", marginStart: 4, marginEnd: 4 },
    },
    crosshair: {
      show: true,
      horizontal: { show: true, line: { show: true, style: "dashed", dashedValue: [4, 2], size: 1, color: "rgba(255,255,255,0.35)" }, text: { color: "#ffffff", size: 10, family: "IBM Plex Mono", backgroundColor: "#1e293b" } },
      vertical:   { show: true, line: { show: true, style: "dashed", dashedValue: [4, 2], size: 1, color: "rgba(255,255,255,0.35)" }, text: { color: "#ffffff", size: 10, family: "IBM Plex Mono", backgroundColor: "#1e293b" } },
    },
    separator: { size: 1, color: theme.grid, fill: true, activeBackgroundColor: theme.grid },
  };
}

function ChartPanel({ candles, zones, structure, symbol, timeframe, theme, onCrosshair, syncedCrosshair, panelId, showKillZones, showMarkers, activeIndicators, zoneStyles, onChartReady }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const overlayIdsRef = useRef([]);
  const indicatorPanesRef = useRef({}); // { name: paneId }

  const normalizedCandles = useMemo(() => normalizeCandles(candles), [candles]);

  // All SVG overlay math (kill-zones, structure boxes, FVG, swings, CRT, impulses)
  // is delegated to a dedicated hook — see /hooks/useChartOverlays.js
  const {
    overlayRects,
    killZoneBands,
    instMarkers,
    structureBoxes,
    cisdMarkers,
    fvgBoxes,
    swingPoints,
    crtBoxes,
  } = useChartOverlays({
    chartRef,
    containerRef,
    candles,
    zones,
    structure,
    showKillZones,
    showMarkers,
    activeIndicators,
    theme,
    zoneStyles,
  });

  // Create/re-create chart whenever the identity changes. Keying by symbol+timeframe
  // (not just first mount) guarantees a clean klinecharts instance whenever the user
  // switches assets — critical because `applyNewData` alone keeps the previous Y-axis
  // scale, so EURUSD (~1.14) → XAUUSD (~4053) leaves candles rendered off-screen.
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = init(containerRef.current, { styles: buildStyles(theme), locale: "en-US" });
    chartRef.current = chart;
    onChartReady?.(chart);

    if (onCrosshair) {
      chart.subscribeAction("onCrosshairChange", (data) => {
        if (data && data.kLineData) onCrosshair({ timestamp: data.kLineData.timestamp, panelId });
        else onCrosshair(null);
      });
    }

    return () => {
      try { dispose(containerRef.current); } catch { /* noop */ }
      chartRef.current = null;
      indicatorPanesRef.current = {};
    };
  }, [symbol, timeframe]);

  // Apply theme
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.setStyles(buildStyles(theme));
  }, [theme]);

  // Sync active indicators (create/remove) — only klinecharts native ones
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const nativeWanted = new Set(
      Array.from(activeIndicators).filter((n) => INDICATOR_MAP[n] && !INDICATOR_MAP[n].custom)
    );
    const current = new Set(Object.keys(indicatorPanesRef.current));
    // Remove indicators no longer active
    for (const name of current) {
      if (!nativeWanted.has(name)) {
        try { chart.removeIndicator(indicatorPanesRef.current[name], name); } catch { /* noop */ }
        delete indicatorPanesRef.current[name];
      }
    }
    // Add newly-activated indicators
    for (const name of nativeWanted) {
      if (!current.has(name)) {
        const meta = INDICATOR_MAP[name];
        if (!meta) continue;
        try {
          const paneId = meta.overlay
            ? chart.createIndicator(name, false, { id: "candle_pane" })
            : chart.createIndicator(name, false, {});
          if (paneId) indicatorPanesRef.current[name] = paneId;
        } catch { /* noop */ }
      }
    }
  }, [activeIndicators]);

  // Push data — chart lifecycle is tied to symbol/timeframe via the init effect
  // above, so this effect only handles data refresh within the same chart instance
  // (e.g. auto-refresh 30s or new API response for the same asset).
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.applyNewData(normalizedCandles);
    chart.scrollToRealTime?.();
  }, [normalizedCandles]);

  // RL price lines (klinecharts native overlays) — gated by the RL toggle
  useEffect(() => {
    if (!chartRef.current) return;
    // Remove old
    overlayIdsRef.current.forEach((id) => chartRef.current.removeOverlay(id));
    overlayIdsRef.current = [];
    if (!activeIndicators?.has("RL")) return;
    (zones?.RL || []).forEach((r) => {
      const id = chartRef.current.createOverlay({
        name: "priceLine",
        points: [{ value: r.price }],
        styles: { priceLine: { style: "dashed", dashedValue: [4, 2], size: 1, color: "#a78bfa" } },
      });
      if (id) overlayIdsRef.current.push(id);
    });
  }, [zones, activeIndicators]);

  // === SVG overlays are now driven by the useChartOverlays hook above ===

  // Sync crosshair from other panel — klinecharts doesn't expose setCrosshairPosition,
  // so we render a visual synchronized vertical line via a small SVG hint
  const [syncLineX, setSyncLineX] = useState(null);
  useEffect(() => {
    if (!syncedCrosshair || !chartRef.current || syncedCrosshair.panelId === panelId) {
      setSyncLineX(null);
      return;
    }
    const p = chartRef.current.convertToPixel(
      [{ timestamp: syncedCrosshair.timestamp, value: 0 }],
      { paneId: "candle_pane" }
    );
    const pt = Array.isArray(p) ? p[0] : p;
    if (pt && pt.x != null) setSyncLineX(pt.x);
  }, [syncedCrosshair, panelId]);

  return (
    <div className="relative w-full h-full" data-testid={`chart-panel-${panelId}`}>
      {/* key= forces React to build a fresh DOM node whenever the symbol/timeframe
          changes — critical because klinecharts caches its instance keyed by DOM
          element; reusing the same node means our fresh init() picks up the stale
          instance. Rebuilding the node guarantees a clean canvas. */}
      <div key={`${symbol}-${timeframe}`} ref={containerRef} className="absolute inset-0" style={{ zIndex: 1 }} />
      {/* Custom watermark */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.05] select-none" style={{ zIndex: 5 }}>
        <div className="font-heading font-bold text-6xl tracking-widest text-white">ANATRADEX</div>
      </div>
      <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%" style={{ zIndex: 10 }}>
        {killZoneBands.map((b) => (
          <g key={b.id}>
            <rect x={b.x} y={b.y} width={Math.max(1, b.w)} height={Math.max(1, b.h)} fill={b.fill} stroke={b.stroke} strokeWidth="1" strokeDasharray="3 2" opacity="0.95" />
            <text x={b.labelX} y={b.labelY} fontSize="9" fill={b.stroke} fontFamily="IBM Plex Mono" fontWeight="600" textAnchor="middle" opacity="0.9">{b.label}</text>
          </g>
        ))}
        {overlayRects.map((r) => (
          <g key={r.id}>
            <rect x={r.x} y={r.y} width={Math.max(0, r.w)} height={Math.max(1, r.h)} fill={r.color} stroke={r.stroke} strokeWidth="0.8" />
            <text x={r.x + 4} y={r.y + 11} fontSize="9" fill="rgba(255,255,255,0.75)" fontFamily="IBM Plex Mono">{r.label}</text>
          </g>
        ))}
        {/* Market Structure: Order Blocks + Breaker Blocks */}
        {structureBoxes.map((b) => (
          <g key={b.id}>
            <rect x={b.x} y={b.y} width={Math.max(0, b.w)} height={Math.max(1, b.h)} fill={b.color} stroke={b.stroke} strokeWidth="1" strokeDasharray="4 2" />
            <text x={b.x + 4} y={b.y + 11} fontSize="9" fill={b.stroke} fontFamily="IBM Plex Mono" fontWeight="700">{b.label}</text>
          </g>
        ))}
        {/* Fair Value Gaps */}
        {fvgBoxes.map((f) => (
          <g key={f.id}>
            <rect x={f.x} y={f.y} width={Math.max(0, f.w)} height={Math.max(1, f.h)} fill={f.color} stroke={f.stroke} strokeWidth="0.8" strokeDasharray="1 2" />
            <text x={f.x + 4} y={f.y + 10} fontSize="8" fill={f.stroke} fontFamily="IBM Plex Mono" fontWeight="600">{f.label}</text>
          </g>
        ))}
        {/* CRT boxes */}
        {crtBoxes.map((c) => (
          <g key={c.id}>
            <rect x={c.x} y={c.y} width={Math.max(1, c.w)} height={Math.max(1, c.h)} fill={c.color} stroke={c.stroke} strokeWidth="1" />
            <text x={c.x + c.w / 2} y={c.y - 3} fontSize="8" fill={c.stroke} fontFamily="IBM Plex Mono" fontWeight="700" textAnchor="middle">{c.label}</text>
          </g>
        ))}
        {/* Swing High/Low markers */}
        {swingPoints.map((s) => {
          const isHigh = s.type === "high";
          const color = isHigh ? "#f59e0b" : "#8b5cf6";
          return (
            <g key={s.id}>
              <circle cx={s.x} cy={s.y} r="3" fill={color} stroke="#000" strokeWidth="0.5" />
              <text x={s.x} y={isHigh ? s.y - 6 : s.y + 12} fontSize="7" fill={color} fontFamily="IBM Plex Mono" fontWeight="700" textAnchor="middle">{isHigh ? "SH" : "SL"}</text>
            </g>
          );
        })}
        {/* CISD markers — vertical flag with BOS/CHoCH label */}
        {cisdMarkers.map((c) => {
          const isBull = c.direction === "bull";
          const color = isBull ? "#10b981" : "#f43f5e";
          const flagY = isBull ? c.y + 12 : c.y - 20;
          return (
            <g key={c.id}>
              <line x1={c.x} y1={c.y - 60} x2={c.x} y2={c.y + 60} stroke={color} strokeWidth="1.2" strokeDasharray="2 2" opacity="0.7" />
              <rect x={c.x - 22} y={flagY - 8} width="44" height="14" fill={color} fillOpacity="0.85" rx="2" />
              <text x={c.x} y={flagY + 2} fontSize="8" fill="#000" fontFamily="IBM Plex Mono" fontWeight="700" textAnchor="middle">CISD {isBull ? "▲" : "▼"}</text>
            </g>
          );
        })}
        {instMarkers.map((m) => {
          const isBuy = m.side === "buy";
          const color = isBuy ? (theme.impulseBuy || "#10b981") : (theme.impulseSell || "#f43f5e");
          const h = Math.max(4, m.yBot - m.yTop);
          const label = isBuy ? "ACHAT" : "VENTE";
          return (
            <g key={m.id}>
              <rect x={m.x} y={m.yTop - 2} width={8} height={h + 4} fill={color} fillOpacity="0.35" stroke={color} strokeWidth="1" rx="1" />
              <text x={m.x + 12} y={isBuy ? m.yBot + 8 : m.yTop - 4} fontSize="8" fill={color} fontFamily="IBM Plex Mono" fontWeight="700">{label}·σ{m.zScore}</text>
            </g>
          );
        })}
        {syncLineX != null && (
          <line x1={syncLineX} y1={0} x2={syncLineX} y2="100%" stroke="rgba(255,255,255,0.35)" strokeWidth="1" strokeDasharray="3 3" />
        )}
      </svg>
    </div>
  );
}

export default function ProCandleChart({ candles, zones, structure, symbol, timeframe, onTimeframeChange, allTimeframes = [], paneCount = 1, onPaneCountChange, paneTfs = [], setPaneTf, paneData = [], paneLoading = [], availableSplitTfs = [] }) {
  const wrapRef = useRef(null);
  const mainChartRef = useRef(null);
  const [theme, setTheme] = useState(loadTheme());
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [syncedCrosshair, setSyncedCrosshair] = useState(null);
  const [showKillZones, setShowKillZones] = useState(() => { try { return localStorage.getItem("qtr-chart-killzones") === "1"; } catch { return false; } });
  const [showMarkers, setShowMarkers] = useState(() => { try { return localStorage.getItem("qtr-chart-markers") === "1"; } catch { return false; } });
  const [activeIndicators, setActiveIndicators] = useState(() => {
    try {
      const raw = localStorage.getItem("qtr-chart-indicators");
      if (raw) {
        const s = new Set(JSON.parse(raw));
        // One-time migration: ZII/ZRI/DP/RL became toggleable — keep them visible
        if (!localStorage.getItem("qtr-zones-migrated")) {
          ["ZII", "ZRI", "DP", "RL"].forEach((z) => s.add(z));
          localStorage.setItem("qtr-zones-migrated", "1");
          localStorage.setItem("qtr-chart-indicators", JSON.stringify(Array.from(s)));
        }
        return s;
      }
    } catch { /* noop */ }
    return new Set(DEFAULT_ACTIVE);
  });
  const [zoneStyles, setZoneStyles] = useState(loadZoneStyles);

  const updateZoneStyles = (next) => { setZoneStyles(next); saveZoneStyles(next); };

  // Templates — snapshot & restore of the FULL chart configuration
  const templateSnapshot = () => ({
    theme,
    indicators: Array.from(activeIndicators),
    zoneStyles,
    showKillZones,
    showMarkers,
  });
  const applyTemplate = (t) => {
    if (t.theme) { setTheme({ ...DEFAULT_THEME, ...t.theme }); saveTheme({ ...DEFAULT_THEME, ...t.theme }); }
    if (t.indicators) { const s = new Set(t.indicators); setActiveIndicators(s); persistIndicators(s); }
    if (t.zoneStyles) updateZoneStyles(t.zoneStyles);
    if (typeof t.showKillZones === "boolean") {
      setShowKillZones(t.showKillZones);
      try { localStorage.setItem("qtr-chart-killzones", t.showKillZones ? "1" : "0"); } catch { /* noop */ }
    }
    if (typeof t.showMarkers === "boolean") {
      setShowMarkers(t.showMarkers);
      try { localStorage.setItem("qtr-chart-markers", t.showMarkers ? "1" : "0"); } catch { /* noop */ }
    }
  };

  const persistIndicators = (setLike) => {
    try { localStorage.setItem("qtr-chart-indicators", JSON.stringify(Array.from(setLike))); } catch { /* noop */ }
  };

  const toggleIndicator = (name) => {
    setActiveIndicators((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      persistIndicators(next);
      return next;
    });
  };
  const resetIndicators = () => {
    const next = new Set(DEFAULT_ACTIVE);
    setActiveIndicators(next);
    persistIndicators(next);
  };
  const applyIndicatorPreset = (indicatorList) => {
    const next = new Set(indicatorList);
    setActiveIndicators(next);
    persistIndicators(next);
  };

  const updateTheme = (patch) => { const next = { ...theme, ...patch }; setTheme(next); saveTheme(next); };
  const applyPreset = (name) => { const next = { ...PRESETS[name] }; setTheme(next); saveTheme(next); };
  const resetTheme = () => { setTheme(DEFAULT_THEME); saveTheme(DEFAULT_THEME); };
  const toggleKillZones = () => setShowKillZones((v) => { const n = !v; try { localStorage.setItem("qtr-chart-killzones", n ? "1" : "0"); } catch { /* noop */ } return n; });
  const toggleMarkers = () => setShowMarkers((v) => { const n = !v; try { localStorage.setItem("qtr-chart-markers", n ? "1" : "0"); } catch { /* noop */ } return n; });

  // Auto-detect Fibonacci — uses the last CISD level as anchor (bull=up move, bear=down move)
  const autoDetectFib = () => {
    const chart = mainChartRef.current;
    if (!chart || !candles || candles.length < 20) return;
    // Find the highest high and lowest low in the last 60 candles = swing range
    const recent = candles.slice(-60);
    let hi = { price: -Infinity, time: null }, lo = { price: Infinity, time: null };
    for (const c of recent) {
      const t = c.time > 1e12 ? c.time : c.time * 1000;
      if (c.high > hi.price) hi = { price: c.high, time: t };
      if (c.low < lo.price)  lo = { price: c.low,  time: t };
    }
    if (!hi.time || !lo.time) return;
    // Chronological order: earlier point first, later point second
    const [p1, p2] = hi.time < lo.time ? [{ timestamp: hi.time, value: hi.price }, { timestamp: lo.time, value: lo.price }]
                                       : [{ timestamp: lo.time, value: lo.price }, { timestamp: hi.time, value: hi.price }];
    try {
      chart.createOverlay({ name: "fibonacciLine", points: [p1, p2], groupId: "user-draw" });
    } catch { /* noop */ }
  };

  const toggleFullscreen = async () => {
    if (!wrapRef.current) return;
    if (!document.fullscreenElement) await wrapRef.current.requestFullscreen();
    else await document.exitFullscreen();
  };
  useEffect(() => {
    const on = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  const splitActive = paneCount > 1;
  const onCloseSplit = () => onPaneCountChange?.(1);
  const chartMinH = isFullscreen ? "calc(100vh - 110px)" : "460px";
  const gridClass =
    paneCount === 1 ? "grid-cols-1" :
    paneCount === 2 ? "grid-cols-1 xl:grid-cols-2" :
    paneCount === 4 ? "grid-cols-1 md:grid-cols-2" :
    paneCount === 6 ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" :
    "grid-cols-1 md:grid-cols-2 xl:grid-cols-4";
  const paneMinH = paneCount >= 4 ? "min-h-[280px]" : "min-h-[460px]";

  return (
    <div ref={wrapRef} className="relative flex flex-col bg-transparent" data-testid="pro-candle-chart">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-white/60">
          <span className="font-mono uppercase tracking-widest">{symbol}</span>
          <span className="opacity-40">·</span>
          <span className="font-mono">{timeframe}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <button type="button" onClick={() => setThemeMenuOpen((v) => !v)} className="chip text-xs flex items-center gap-1.5 hover:bg-white/5" data-testid="chart-theme-btn" title="Personnaliser les couleurs">
              <Palette size={12} /> Thème
            </button>
            {themeMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 panel p-3 w-72 space-y-3" data-testid="chart-theme-menu">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-widest text-white/60">Thème du graphique</div>
                  <button onClick={() => setThemeMenuOpen(false)} className="text-white/50 hover:text-white"><X size={13} /></button>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {Object.keys(PRESETS).map((name) => (
                    <button key={name} onClick={() => applyPreset(name)} className="text-[10px] py-1 px-1.5 rounded-md border border-white/10 hover:border-accent-blue hover:bg-white/5 capitalize" data-testid={`chart-preset-${name}`}>{name}</button>
                  ))}
                </div>
                <div className="space-y-2.5">
                  <ColorSwatchPicker label="Bougie haussière" value={theme.bullish} onChange={(v) => updateTheme({ bullish: v })} palette="bullish" testid="color-bullish" />
                  <ColorSwatchPicker label="Bougie baissière" value={theme.bearish} onChange={(v) => updateTheme({ bearish: v })} palette="bearish" testid="color-bearish" />
                  <ColorSwatchPicker label="Arrière-plan" value={theme.background} onChange={(v) => updateTheme({ background: v })} palette="background" testid="color-background" />
                  <ColorSwatchPicker label="Grille" value={theme.grid} onChange={(v) => updateTheme({ grid: v })} palette="grid" testid="color-grid" />
                </div>
                <div className="pt-2 border-t border-white/5 space-y-2.5">
                  <div className="text-[9px] font-semibold uppercase tracking-widest text-white/40">Kill-zones</div>
                  <ColorSwatchPicker label="Asia"   value={theme.kzAsia}   onChange={(v) => updateTheme({ kzAsia: v })}   palette="bullish" testid="color-kz-asia" />
                  <ColorSwatchPicker label="London" value={theme.kzLondon} onChange={(v) => updateTheme({ kzLondon: v })} palette="bullish" testid="color-kz-london" />
                  <ColorSwatchPicker label="NY AM"  value={theme.kzNyAm}   onChange={(v) => updateTheme({ kzNyAm: v })}   palette="bullish" testid="color-kz-nyam" />
                  <ColorSwatchPicker label="NY PM"  value={theme.kzNyPm}   onChange={(v) => updateTheme({ kzNyPm: v })}   palette="bearish" testid="color-kz-nypm" />
                </div>
                <div className="pt-2 border-t border-white/5 space-y-2.5">
                  <div className="text-[9px] font-semibold uppercase tracking-widest text-white/40">Impulsions</div>
                  <ColorSwatchPicker label="Impulsion Achat"  value={theme.impulseBuy}  onChange={(v) => updateTheme({ impulseBuy: v })}  palette="bullish" testid="color-impulse-buy" />
                  <ColorSwatchPicker label="Impulsion Vente"  value={theme.impulseSell} onChange={(v) => updateTheme({ impulseSell: v })} palette="bearish" testid="color-impulse-sell" />
                </div>
                <button onClick={resetTheme} className="w-full text-[11px] py-1.5 rounded-md border border-white/10 hover:border-white/30 flex items-center justify-center gap-1.5" data-testid="chart-theme-reset">
                  <RotateCcw size={11} /> Réinitialiser
                </button>
              </div>
            )}
          </div>

          {availableSplitTfs.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setLayoutMenuOpen((v) => !v)}
                className={`chip text-xs flex items-center gap-1.5 ${paneCount > 1 ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "hover:bg-white/5"}`}
                data-testid="chart-split-btn"
                title="Diviser le graphique en plusieurs panneaux avec TF indépendantes"
              >
                <Columns2 size={12} /> Layout {paneCount > 1 ? `(${paneCount})` : ""}
              </button>
              {layoutMenuOpen && (
                <div className="absolute right-0 top-full mt-1 z-[60] panel p-3 w-48" data-testid="chart-layout-menu">
                  <div className="text-xs font-semibold uppercase tracking-widest text-white/60 mb-2">Panneaux</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[1, 2, 4, 6, 8].map((n) => (
                      <button
                        key={n}
                        onClick={() => { onPaneCountChange?.(n); setLayoutMenuOpen(false); }}
                        className={`text-xs py-1.5 rounded-md border ${paneCount === n ? "border-cyan-500 bg-cyan-500/15 text-cyan-300" : "border-white/10 hover:border-white/30"}`}
                        data-testid={`chart-layout-${n}`}
                      >
                        {n === 1 ? "1×" : `${n} panneaux`}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] text-white/40 mt-2">
                    Chaque panneau a sa propre unité de temps (TF) indépendante. Crosshair synchronisé.
                  </div>
                </div>
              )}
            </div>
          )}

          <IndicatorsMenu active={activeIndicators} onToggle={toggleIndicator} onReset={resetIndicators} onApplyPreset={applyIndicatorPreset} zoneStyles={zoneStyles} onZoneStylesChange={updateZoneStyles} />
          <TemplatesMenu getSnapshot={templateSnapshot} onApply={applyTemplate} />
          <DrawingMenu getChart={() => mainChartRef.current} autoDetectFib={autoDetectFib} isAutoDetectAvailable={!!candles?.length} />

          <button type="button" onClick={toggleKillZones} className={`chip text-xs flex items-center gap-1.5 ${showKillZones ? "bg-accent-blue/20 border-accent-blue/50 text-accent-blue" : "hover:bg-white/5"}`} data-testid="chart-killzones-btn" title="Kill-zones institutionnelles (Asia/London/NY)">
            <Layers size={12} /> Kill-zones
          </button>
          <button type="button" onClick={toggleMarkers} className={`chip text-xs flex items-center gap-1.5 ${showMarkers ? "bg-purple-500/20 border-purple-500/50 text-purple-300" : "hover:bg-white/5"}`} data-testid="chart-markers-btn" title="Impulsions institutionnelles — bougies achat (vert) et vente (rouge) avec pic de volume Z-score > 1.6σ">
            <Zap size={12} /> Impulsions
          </button>

          {splitActive && (
            <>
              <button type="button" onClick={onCloseSplit} className="chip text-xs flex items-center gap-1.5 hover:bg-red-500/10" data-testid="chart-split-close" title="Fermer les splits">
                <X size={12} />
              </button>
            </>
          )}

          <button type="button" onClick={toggleFullscreen} className="chip text-xs flex items-center gap-1.5 hover:bg-white/5" data-testid="chart-fullscreen-btn" title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}>
            {isFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
        </div>
      </div>

      {/* Chart panels — 1..8 in a responsive grid */}
      <div className={`grid gap-2 flex-1 ${gridClass}`} style={{ minHeight: chartMinH }}>
        {/* Main pane (pane 0) */}
        <PaneWrap paneMinH={paneMinH} theme={theme}>
          {/* Inline TF selector on the main pane */}
          {onTimeframeChange && allTimeframes.length > 0 && (
            <div className="absolute top-2 left-2 z-20">
              <select
                value={timeframe}
                onChange={(e) => onTimeframeChange(e.target.value)}
                className="field-sm text-[10px] px-1.5 py-1 border border-white/20 bg-black/60 backdrop-blur font-semibold"
                data-testid="pane-tf-main"
                title="Unité de temps du graphique principal"
              >
                {allTimeframes.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
              </select>
            </div>
          )}
          <ChartPanel candles={candles} zones={zones} structure={structure} symbol={symbol} timeframe={timeframe} theme={theme} onCrosshair={splitActive ? setSyncedCrosshair : null} syncedCrosshair={syncedCrosshair} panelId="main" showKillZones={showKillZones} showMarkers={showMarkers} activeIndicators={activeIndicators} zoneStyles={zoneStyles} onChartReady={(c) => { mainChartRef.current = c; }} />
        </PaneWrap>
        {/* Extra panes (1..N-1) with per-pane TF selector */}
        {Array.from({ length: Math.max(0, paneCount - 1) }).map((_, idx) => {
          const paneTf = paneTfs[idx] || "H4";
          const d = paneData[idx];
          const loading = paneLoading[idx];
          return (
            <PaneWrap key={`p-${idx}`} paneMinH={paneMinH} theme={theme}>
              {/* Per-pane TF selector overlay in top-left corner */}
              <div className="absolute top-2 left-2 z-20">
                <select
                  value={paneTf}
                  onChange={(e) => setPaneTf?.(idx, e.target.value)}
                  className="field-sm text-[10px] px-1.5 py-1 border border-white/20 bg-black/60 backdrop-blur"
                  data-testid={`pane-tf-${idx}`}
                >
                  {availableSplitTfs.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                </select>
              </div>
              {loading && !d ? (
                <div className="absolute inset-0 grid place-items-center text-white/40 text-xs">Chargement {paneTf}…</div>
              ) : (
                <ChartPanel candles={d?.candles} zones={d?.zones} structure={d?.structure} symbol={symbol} timeframe={paneTf} theme={theme} onCrosshair={setSyncedCrosshair} syncedCrosshair={syncedCrosshair} panelId={`pane-${idx}`} showKillZones={showKillZones} showMarkers={showMarkers} activeIndicators={activeIndicators} zoneStyles={zoneStyles} />
              )}
            </PaneWrap>
          );
        })}
      </div>
    </div>
  );
}

function PaneWrap({ children, paneMinH, theme }) {
  return (
    <div className={`relative w-full h-full ${paneMinH}`} style={{ background: theme.background, borderRadius: 8, overflow: "hidden" }}>
      {children}
    </div>
  );
}
